import type { LogContext } from './types';

const SANITISE_KEYS = new Set([
    "token",
    "accessToken",
    "authorization",
    "cookie",
    "password",
    "secret"
])

const SANITISE_STRING_PATTERNS = [
    { pattern: /Bearer\s+[^\s"',}{)]+/g, replacement: 'Bearer *****' },
    {
        pattern: /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
        replacement: '[JWT_REDACTED]',
    },
    {
        pattern: new RegExp(
            '([Tt]oken|[Kk]ey|[Ss]ecret|[Pp]assword|[Aa]uthorization)[=:\\s]*["\']?[^\\s"\',}{)]+',
            'g',
        ),
        replacement: '$1=*****',
    },
]

type SanitisedValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | SanitisedValue[]
    | { [key: string]: SanitisedValue };

/**
 * Sanitises context object by stripping any of the keys that are in the SANITISE_KEYS set.
 */
export function sanitise(context: unknown): LogContext | undefined {
    if (!context || typeof context !== 'object') {
        return undefined;
    }
    const result = sanitiseValue(context, new WeakSet<object>());
    if (!result || typeof result !== 'object' || Array.isArray(result)) {
        return undefined;
    }

    return result as LogContext;
}

function sanitiseString(str: string): string {
    let result = str;
    for (const { pattern, replacement } of SANITISE_STRING_PATTERNS) {
        result = result.replace(pattern, replacement);
    }
    return result;
}

/**
 * Recursively sanitises values in the object by replacing sensitive keys
 * with "[REDACTED]", scrubbing sensitive string patterns,
 * and replacing circular references with "[Circular]".
 */
function sanitiseValue(value: unknown, seen: WeakSet<object>): SanitisedValue {
    if (value == null || typeof value !== 'object') {
        if (typeof value === 'string') {
            return sanitiseString(value);
        }
        return value as SanitisedValue;
    }

    if (seen.has(value)) {
        return '[Circular]';
    }

    seen.add(value);

    if (Array.isArray(value)) {
        return value.map((item) => sanitiseValue(item, seen));
    }

    const sanitisedObject: { [key: string]: SanitisedValue } = {};

    for (const [key, nestedValue] of Object.entries(value)) {
        if (SANITISE_KEYS.has(key)) {
            sanitisedObject[key] = "[REDACTED]";
        } else {
            sanitisedObject[key] = sanitiseValue(nestedValue, seen);
        }
    }

    return sanitisedObject;
}

export function stringToContext(inputString: unknown, valueLabel = 'value'): LogContext {
    if (inputString === null || inputString === undefined) {
        return {};
    }

    if (typeof inputString !== 'string') {
        return { [valueLabel]: inputString };
    }

    const trimmedValue = inputString.trim();

    try {
        const parsed = JSON.parse(trimmedValue);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as LogContext;
        }

        return { [valueLabel]: parsed };
    } catch {
        return { [valueLabel]: trimmedValue }
    }
}

